import * as React from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import UserList from "./UserList";
import CallWindowIcon from "./CallWindowIcon";
import dayjs from "dayjs";
import EventDetail from "./EventDetail";
import SessionPopup from "../common/SessionPopup";
import DiscordDraftSection from "../AddActivityWindow/DiscordDraftSection";
import { useAuth } from "../../contexts/AuthContext";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

const EventCard = ({ session, onAddActivity, isAdminView }) => {
    const { groupMembers } = useAuth();
    const { title, contributors, date, startTime, endTime, labelColor, files, isCrossDate, editTime, prMeta, prLink, googleDocs, figmaFiles, discord } = session;
    const cardRef = React.useRef(null);
    const [showIcon, setShowIcon] = React.useState(false);
    const isWindowOpen = React.useRef(false);

    const [showPopup, setShowPopup] = React.useState(false);
    const [showDiscordPopup, setShowDiscordPopup] = React.useState(false);
    const [discordPopupData, setDiscordPopupData] = React.useState(null);
    useBodyScrollLock(showDiscordPopup);
    const openFileInNewTab = (url) => {
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
            newWindow.focus();
        } else {
            alert('Pop-up blocker may have prevented opening the file. Please check your browser settings and try again.');
        }
    };
    const isEditedAfterCurrentDate = (
        dayjs(session.created_at.toDate()).startOf('day').isAfter(dayjs(date).startOf('day')) &&
        (!isCrossDate || dayjs(session.created_at.toDate()).startOf('day').diff(dayjs(date).startOf('day'), 'day') > 1)
    );

    return (
        <ColorWrapper $color={labelColor} ref={cardRef} style={{ backgroundColor: labelColor }}>
            <CardContent $color={labelColor}>
                <FirstRow>
                    <TitleContainer title={title}>
                        <CardTitle>{title}</CardTitle>
                    </TitleContainer>
                    <IconWrapper
                        onMouseEnter={() => setShowIcon(true)}
                        onMouseLeave={() => setShowIcon(false)}
                    >
                        {(editTime !== 0 && !showIcon && !isWindowOpen.current) && (
                            <EditTime
                                onMouseEnter={() => setShowIcon(true)}
                                onMouseLeave={() => setShowIcon(false)}
                            >
                                {editTime}
                            </EditTime>
                        )}
                        {!isAdminView && (
                            (showIcon || editTime === 0 || isWindowOpen.current) && (
                                <CallWindowIcon
                                    icon="/images/edit.svg"
                                    date={date}
                                    onAddActivity={onAddActivity}
                                    isEditMode={true}
                                    setShowIcon={setShowIcon}
                                    isWindowOpen={isWindowOpen}
                                    sessionData={session}
                                    style={{
                                        aspectRatio: "1",
                                        width: "15px",
                                        opacity: 0.5,
                                        zIndex: 100,
                                    }}
                                />
                            )
                        )}
                    </IconWrapper>
                </FirstRow>
                <UserList contributors={contributors} />
                <EventDetail
                    startTime={startTime}
                    endTime={endTime}
                    isCrossDate={isCrossDate}
                    files={files}
                    prLink={prLink}
                    prMeta={prMeta}
                    googleDocs={googleDocs}
                    figmaFiles={figmaFiles}
                    discordDraft={discord}
                    onDiscordClick={(data) => {
                        setDiscordPopupData(data);
                        setShowDiscordPopup(true);
                    }}
                />
                <LastEditRow $isRed={isEditedAfterCurrentDate}>
                    Last edited: {dayjs(session.created_at.toDate()).format("YYYY-MM-DD HH:mm")}
                </LastEditRow>
                <IconAWrapper
                    onClick={() => setShowPopup(true)}
                >
                    <IconA src="/images/details.svg" alt="" />
                </IconAWrapper>
                {showPopup && (
                    <SessionPopup
                        session={session}
                        onClose={() => setShowPopup(false)}
                        onFileDownload={(url, filename) => openFileInNewTab(url)}
                    />
                )}
                {showDiscordPopup && discordPopupData && ReactDOM.createPortal(
                    <DiscordPopupOverlay onClick={() => setShowDiscordPopup(false)}>
                        <DiscordPopupCard onClick={(e) => e.stopPropagation()}>
                            <DiscordPopupHeader>
                                <DiscordPopupTitle>Discord chat</DiscordPopupTitle>
                                <DiscordCloseBtn type="button" onClick={() => setShowDiscordPopup(false)} aria-label="Close">
                                    ×
                                </DiscordCloseBtn>
                            </DiscordPopupHeader>
                            <DiscordPopupBody>
                                <DiscordDraftSection
                                    discordDraft={discordPopupData}
                                    maxHeight={360}
                                    selectable={false}
                                    groupMembers={groupMembers}
                                />
                            </DiscordPopupBody>
                        </DiscordPopupCard>
                    </DiscordPopupOverlay>,
                    document.body
                )}
            </CardContent>
             <CornerDecor $color={labelColor} />
        </ColorWrapper>
    );
};

const FirstRow = styled.div`
    //background-color: #dddfe2;
    display: flex;
    //align-items: center;
    justify-content: space-between;
    min-height: 30px;
`;

const IconWrapper = styled.div`
    position: relative;
    width: 25px;
    height: 25px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: -13px;
    margin-right: -3px;
    z-index: 100;
    //background-color: rgba(133, 159, 166, 0.24);
`;

const EditTime = styled.span`
    font-size: 15px;
    margin-top: 6px;
    color: #c51212;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    padding-bottom: 2px;
`;

const LastEditRow = styled.div`
    display: flex;
    font: 500 13px Inter, sans-serif;
    color: ${props => (props.$isRed ? '#c51212' : '#969a97')};
    margin-left: 8px;
    padding-bottom: 4px;
    margin-top: 8px;
`;

const IconAWrapper = styled.div`
    position: absolute;
    right: -2px;
    bottom: -7px;
    z-index: 100;
    opacity: 0.5;
    cursor: pointer;
`;

const IconA = styled.img`
    aspect-ratio: 1;
    width: 19px;
    opacity: 0.5;
    //padding-right: 2px;
    margin-right: 4px;
    margin-bottom: 2px;
    //background-color: #65676b;
`;

const ColorWrapper = styled.article`
    border-radius: 5px;
    width: 100%;
    position: relative;
    overflow: visible;
    background: none; /* no stripe */
`;

const CardContent = styled.div`
    border-radius: 5px;
    background-color: #fff;
    display: flex;
    width: 100%;
    flex-direction: column;
    padding: 12px 0px 12px 6px;
    position: relative;
    overflow: visible;
    border: 1px solid ${props => props.$color || '#e5e7eb'};
    box-shadow: 
        0 2px 10px rgba(0, 0, 0, 0.08),
        0 0 8px ${props => props.$color || '#4CAF50'};

`;

const CornerDecor = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;          /* <— clip the decor, not the content */
  pointer-events: none;      /* clicks pass through */
  z-index: 2;                /* above content; tooltips can set higher z-index */

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 57px;
    height: 35px;
    background-color: ${p => p.$color || 'rgb(128,128,128)'};
    mix-blend-mode: multiply;
    opacity: 0.3;
  }

  &::before {
    top: -10px;
    right: -20px;
    transform: rotate(45deg);
  }

  &::after {
    bottom: -10px;
    right: -20px;
    transform: rotate(-45deg);
  }
`;

const CardTitle = styled.h2`
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 23px;
    text-align: left;
    margin-left: 10px;
    margin-bottom: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    //background-color: aquamarine;
`;

const TitleContainer = styled.div`
    flex: 1;
    min-width: 0;
    margin-bottom: 4px;
`;

const DiscordPopupOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
`;

const DiscordPopupCard = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const DiscordPopupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
`;

const DiscordPopupTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
`;

const DiscordCloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    color: #64748b;
    cursor: pointer;
    padding: 0 4px;
    &:hover { color: #1e293b; }
`;

const DiscordPopupBody = styled.div`
    padding: 16px 20px;
    overflow-y: auto;
    flex: 1;
`;

export default EventCard;
